import { useState } from "react";
import { JobCard } from "@/components/JobCard";
import { Briefcase, Globe, Eye, ClipboardCopy, Star } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SkillBadge } from "@/components/SkillBadge";
import { JobData } from "@/hooks/jobs/useJobsData";
import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { EntityList } from "@/components/common/EntityList";
import { EntityGrid } from "@/components/common/EntityGrid";
import { DuplicateManager, useDuplicateManager } from "@/components/common";
import { jobMergeConfig } from "@/config/jobMergeConfig";
import { EntityMergeService } from "@/services/entityMergeService";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { JobMassUpdateModal } from "./JobMassUpdateModal";

interface JobOffersListProps {
  jobs: JobData[];
  viewMode: "card" | "list";
  isLoading?: boolean;
  refreshInterval?: number;
  enableAutoRefresh?: boolean;
  onDeleteJobs?: (ids: string[]) => Promise<void>;
  onJobsChange?: () => void;
  favorites?: Set<string>;
}

export const JobOffersList = ({ 
  jobs, 
  viewMode, 
  isLoading = false, 
  refreshInterval, 
  enableAutoRefresh,
  onDeleteJobs,
  onJobsChange,
  favorites = new Set()
}: JobOffersListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast: toastNotification } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isMassUpdateModalOpen, setIsMassUpdateModalOpen] = useState(false);
  const {
    isMultiSelectMode,
    toggleMultiSelect,
    toggleItemSelection,
    handleDeleteSelected,
    clearSelection,
    isSelected,
    selectedCount,
    selectAll
  } = useMultiSelect(jobs, onDeleteJobs || (() => Promise.resolve()));
  const {
    openMergeModal,
    getDuplicateManagerProps
  } = useDuplicateManager(jobs, isSelected, "jobs", jobMergeConfig);
  // Handle job merge
  const handleMerge = async (merged: JobData, sources: JobData[]) => {
    try {
      const result = await EntityMergeService.mergeJobs(merged, sources);
      if (result.success) {
        toast({
          title: "Jobs merged successfully",
          description: `${sources.length} jobs have been merged into one.`,
        });
        // Refresh jobs list
        if (onJobsChange) {
          onJobsChange();
        }
        if (result.redirectPath)
          navigate(result.redirectPath);
        return result;
      } else {
        toast({
          title: "Failed to merge jobs",
          description: result.error || "An error occurred during merge.",
          variant: "destructive",
        });
        // Rethrow error to prevent modal from closing
        throw new Error(result.error || "An error occurred during merge.");
      }
    } catch (error) {
      console.error("Merge error:", error);
      toast({
        title: "Failed to merge jobs",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
      // Rethrow error to prevent modal from closing
      throw error;
    }
  };

  // Mass update handlers
  const handleMassUpdate = () => {
    setIsMassUpdateModalOpen(true);
  };

  const handleMassUpdateComplete = () => {
    setIsMassUpdateModalOpen(false);
    clearSelection();
    onJobsChange?.();
  };

  const selectedItemIds = Array.from(
    new Set(
      jobs
        .filter(job => isSelected(job.id))
        .map(job => job.id)
    )
  );

  const columns = [
    {
      key: "title",
      header: t("jobs.title", "Title"),
      cell: (job) => {
        const isClosed = job.status?.startsWith('closed/');
        return (
          <Link to={`/jobs/${job.id}`} className="hover:underline flex items-center gap-2">
            {/* iOS-style notification badge before job title */}
            {job.newApplicationsCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-red-500 text-white text-xs rounded-sm min-w-[16px] h-4 flex items-center justify-center px-1 font-bold">
                    {job.newApplicationsCount > 99 ? '99+' : job.newApplicationsCount}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {t("jobs.newApplications", "New applications")}
                </TooltipContent>
              </Tooltip>
            )}
            <Briefcase className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            <span className={cn(
              isClosed && "line-through text-gray-500 dark:text-gray-400"
            )}>
              {job.title}
            </span>
            {favorites.has(job.id) && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />}
            {/* Published indicator */}
            {job.published && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="text-green-600 hover:text-green-700 transition-colors"
                    type="button"
                    title="Published - Click for options"
                  >
                    <Globe className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-60 p-3" side="bottom" align="start">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Globe className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-sm">Published Job</span>
                    </div>
                    <div className="space-y-2">
                      <button
                        className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        onClick={() => {
                          window.open(`${window.location.origin}/jobs/${job.id}/public`, '_blank', 'noopener');
                        }}
                      >
                        <Eye className="h-4 w-4 text-blue-500" />
                        <div>
                          <div className="text-sm font-medium">View Public Page</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Open in new tab</div>
                        </div>
                      </button>
                      <button
                        className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/jobs/${job.id}/public`);
                          toastNotification({ 
                            title: 'Copied!', 
                            description: 'Public page URL copied to clipboard.' 
                          });
                        }}
                      >
                        <ClipboardCopy className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        <div>
                          <div className="text-sm font-medium">Copy Link</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Share this job publicly</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </Link>
        );
      },
    },
    {
      key: "company",
      header: t("jobs.company", "Company"),
      cell: (job) => {
        const isClosed = job.status?.startsWith('closed/');
        return (
          <span className={cn(
            isClosed && "text-gray-500 dark:text-gray-400"
          )}>
            {job.company?.name || "-"}
          </span>
        );
      },
    },
    {
      key: "skills",
      header: t("jobs.skills", "Skills"),
      cell: (job) => {
        const isClosed = job.status?.startsWith('closed/');
        return (
          <div className={cn(
            "flex flex-wrap gap-1",
            isClosed && "opacity-60"
          )}>
            {job.skills && job.skills.slice(0, 3).map((skill) => (
              <SkillBadge key={skill} label={skill} />
            ))}
            {job.skills && job.skills.length > 3 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">+{job.skills.length - 3} {t("jobs.more", "more")}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "matchCount",
      header: t("jobs.potentialMatches", "Potential Matches"),
      cell: (job) => {
        const isClosed = job.status?.startsWith('closed/');
        return (
          <span className={cn(
            isClosed && "text-gray-500 dark:text-gray-400"
          )}>
            {job.matchCount ?? "-"}
          </span>
        );
      },
    },
    {
      key: "status",
      header: t("jobs.status", "Status"),
      cell: (job) => {
        const isClosed = job.status?.startsWith('closed/');
        
        // Helper function to get status color
        const getStatusColor = (status: string) => {
          switch (status) {
            case 'open':
              return 'bg-green-100 text-green-700';
            case 'hold':
              return 'bg-yellow-100 text-yellow-700';
            case 'closed/won':
            case 'closed/lost':
              return 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
            default:
              return 'bg-blue-100 text-blue-700';
          }
        };

        if (!job.status) return "-";
        
        return (
          <span className={cn(
            "text-xs px-2 py-1 rounded-sm",
            getStatusColor(job.status),
            isClosed && "opacity-60"
          )}>
            {String(t(`jobs.statuses.${job.status}`, job.status))}
          </span>
        );
      },
    },
    {
      key: "applications",
      header: t("jobs.applications", "Applications"),
      cell: (job) => {
        const isClosed = job.status?.startsWith('closed/');
        return (
          <span className={cn(
            isClosed && "text-gray-500 dark:text-gray-400"
          )}>
            {job.applicationsCount ?? 0}
          </span>
        );
      },
    },
  ];

  let content;
  if (jobs.length === 0) {
    content = (
      <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-sm shadow">
        <p className="text-gray-500 dark:text-gray-400">{t("jobs.noResults", "No job offers found matching your search criteria.")}</p>
      </div>
    );
  } else if (viewMode === 'card') {
    content = (
      <EntityGrid
        data={jobs}
        isLoading={isLoading}
        emptyMessage={t("jobs.noResults", "No job offers found matching your search criteria.")}
        renderItem={(job) => (
          <JobCard 
            key={job.id} 
            job={{ 
              ...job, 
              published: job.published, 
              applicationsCount: job.applicationsCount, 
              processesCount: job.processesCount, 
              status: job.status, 
              newApplicationsCount: job.newApplicationsCount 
            }} 
            isSelected={isSelected(job.id)} 
            isFavorite={favorites.has(job.id)} 
          />
        )}
        isMultiSelectMode={isMultiSelectMode}
        onToggleMultiSelect={toggleMultiSelect}
        onToggleItemSelection={toggleItemSelection}
        isItemSelected={isSelected}
        onDeleteSelected={() => setShowDeleteDialog(true)}
        onMergeSelected={openMergeModal}
        onMassUpdate={handleMassUpdate}
        selectedCount={selectedCount}
        selectAll={selectAll}
        onCardClick={toggleItemSelection}
      />
    );
  } else {
    content = (
      <EntityList
        data={jobs}
        columns={columns}
        isLoading={isLoading}
        emptyMessage={t("jobs.noResults")}
        basePath="/jobs"
        isMultiSelectMode={isMultiSelectMode}
        onToggleMultiSelect={toggleMultiSelect}
        onToggleItemSelection={toggleItemSelection}
        isItemSelected={isSelected}
        onDeleteSelected={() => setShowDeleteDialog(true)}
        onMergeSelected={openMergeModal}
        onMassUpdate={handleMassUpdate}
        selectedCount={selectedCount}
        selectAll={selectAll}
      />
    );
  }

  return (
    <>
      {content}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.deleteConfirmation", { count: selectedCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.deleteWarning", { count: selectedCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteSelected();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>      <DuplicateManager
        {...getDuplicateManagerProps(handleMerge)}
      />
      <JobMassUpdateModal
        isOpen={isMassUpdateModalOpen}
        onClose={() => setIsMassUpdateModalOpen(false)}
        selectedJobIds={selectedItemIds}
        onUpdate={handleMassUpdateComplete}
      />
    </>
  );
};
